import { useQuery } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import { PublicReadUnavailable, fetchPublicRead } from "@/lib/public-read";

export interface Category {
  id: string;
  name: string;
  created_at: string;
}

export const useCategories = () => {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: async (): Promise<Category[]> => {
      try {
        const cached = await fetchPublicRead<{ data: Category[] }>("categories");
        return cached.data ?? [];
      } catch (error) {
        if (!(error instanceof PublicReadUnavailable)) throw error;
        console.warn("[categories] cached read unavailable, using direct query:", error.message);
      }

      // Cast to any since categories table may not be in generated types yet
      const { data, error } = await (db as any).from("categories")
        .select("*")
        .order("name");

      if (error) {
        throw new Error(`Failed to fetch categories: ${error.message}`);
      }

      return (data || []) as Category[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Helper function to get category names array
export const useCategoryNames = () => {
  const { data: categories, ...rest } = useCategories();
  
  return {
    categoryNames: categories?.map((c) => c.name) || [],
    categories,
    ...rest,
  };
};

